import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ScrollText, Filter, ChevronDown, ChevronRight, User, Clock, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDateTime } from "@/lib/utils-format";
import { AuditChanges } from "@/components/AuditChanges";

const ACTION_META = {
  "disposal.request": { label: "Disposal requested", color: "bg-amber-100 text-amber-800" },
  "disposal.approve": { label: "Disposal approved", color: "bg-red-100 text-red-800" },
  "disposal.reject": { label: "Disposal rejected", color: "bg-slate-200 text-slate-800" },
  "vendor_purchase.direct_dispatch": { label: "Purchase dispatched to showroom", color: "bg-blue-100 text-blue-800" },
  "accounting.period_lock": { label: "Period lock changed", color: "bg-[#2C1810] text-[#F7F2E8]" },
  "product.update": { label: "Product edited", color: "bg-amber-100 text-amber-800" },
  "product.stock_adjustment": { label: "Stock adjusted", color: "bg-orange-100 text-orange-800" },
  "vendor.update": { label: "Vendor edited", color: "bg-amber-100 text-amber-800" },
  "vendor.delete": { label: "Vendor deleted", color: "bg-red-100 text-red-800" },
  "purchase_order.update": { label: "PO edited", color: "bg-amber-100 text-amber-800" },
  "purchase_order.delete": { label: "PO deleted", color: "bg-red-100 text-red-800" },
  "dispatch.update_items": { label: "Dispatch items edited", color: "bg-amber-100 text-amber-800" },
  "expense.delete": { label: "Expense deleted", color: "bg-red-100 text-red-800" },
  "admin.launch_wipe": { label: "Launch wipe", color: "bg-red-100 text-red-800" },
  "admin.full_wipe": { label: "Full wipe", color: "bg-red-100 text-red-800" },
  "dispatch.create": { label: "Dispatch created", color: "bg-emerald-100 text-emerald-800" },
  "dispatch.update": { label: "Dispatch edited", color: "bg-amber-100 text-amber-800" },
  "dispatch.delete": { label: "Dispatch deleted", color: "bg-red-100 text-red-800" },
  "product.delete": { label: "Product deleted", color: "bg-red-100 text-red-800" },
  "product.archive": { label: "Product archived", color: "bg-stone-200 text-stone-800" },
  "product.unarchive": { label: "Product restored", color: "bg-emerald-100 text-emerald-800" },
  "role.create": { label: "Role created", color: "bg-emerald-100 text-emerald-800" },
  "role.update": { label: "Role updated", color: "bg-amber-100 text-amber-800" },
  "role.delete": { label: "Role deleted", color: "bg-red-100 text-red-800" },
  "user.create": { label: "User created", color: "bg-emerald-100 text-emerald-800" },
  "user.update": { label: "User updated", color: "bg-amber-100 text-amber-800" },
  "user.delete": { label: "User deleted", color: "bg-red-100 text-red-800" },
  "user.role_change": { label: "Role changed", color: "bg-purple-100 text-purple-800" },
  "user.permission_override": { label: "Permission override", color: "bg-purple-100 text-purple-800" },
  "auth.2fa_enable": { label: "2FA enabled", color: "bg-emerald-100 text-emerald-800" },
  "auth.2fa_disable": { label: "2FA disabled", color: "bg-red-100 text-red-800" },
  "pos.void": { label: "POS voided", color: "bg-red-100 text-red-800" },
  "pos_sale.update": { label: "POS sale edited", color: "bg-amber-100 text-amber-800" },
  "product.price_change": { label: "Price changed", color: "bg-blue-100 text-blue-800" },
  "settings.update": { label: "Settings changed", color: "bg-amber-100 text-amber-800" },
  "vendor_purchase.create": { label: "Vendor purchase created", color: "bg-emerald-100 text-emerald-800" },
  "vendor_purchase.update": { label: "Vendor purchase edited", color: "bg-amber-100 text-amber-800" },
  "vendor_purchase.delete": { label: "Vendor purchase deleted", color: "bg-red-100 text-red-800" },
  "vendor_purchase.pay": { label: "Vendor payment recorded", color: "bg-emerald-100 text-emerald-800" },
  "vendor_purchase.return": { label: "Purchase return / debit note", color: "bg-purple-100 text-purple-800" },
  "sales_doc.update": { label: "Sales doc edited", color: "bg-amber-100 text-amber-800" },
  "sales_doc.delete": { label: "Sales doc deleted", color: "bg-red-100 text-red-800" },
  "grn.update": { label: "GRN edited (draft)", color: "bg-amber-100 text-amber-800" },
  "grn.update_received": { label: "GRN corrected (received)", color: "bg-amber-100 text-amber-800" },
  "grn.delete": { label: "GRN deleted (draft)", color: "bg-red-100 text-red-800" },
  "grn.delete_received": { label: "GRN deleted (received)", color: "bg-red-100 text-red-800" },
};

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [expanded, setExpanded] = useState(new Set());
  const [deletionsOnly, setDeletionsOnly] = useState(false);

  const load = async () => {
    const params = {};
    if (action !== "all") params.action = action;
    if (entityType !== "all") params.entity_type = entityType;
    const { data } = await api.get("/audit-logs", { params: { limit: 200, ...params } });
    setItems(data.items || []);
    setActions(data.actions || []);
  };

  useEffect(() => { load(); }, [action, entityType]);

  const visibleItems = useMemo(() => (
    deletionsOnly ? items.filter((i) => (i.action || "").endsWith(".delete") || (i.action || "").includes("delete_")) : items
  ), [items, deletionsOnly]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const entityTypes = useMemo(() => {
    const set = new Set(items.map((i) => i.entity_type).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Compliance</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Audit Log</h1>
          <p className="text-sm text-[#8B7355] mt-1">Immutable record of who changed what and when. Covers role changes, permission overrides, POS voids, price edits, 2FA and settings.</p>
        </div>
        <Button
          onClick={async () => {
            try {
              const params = { limit: 5000, format: "csv" };
              if (action !== "all") params.action = action;
              if (entityType !== "all") params.entity_type = entityType;
              const resp = await api.get("/audit-logs", { params, responseType: "blob" });
              const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const stamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, "").replace("T", "-");
              a.download = `bakemill-audit-${stamp}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              /* ignore */
            }
          }}
          data-testid="audit-export-csv"
          variant="outline"
          className="border-[#EAE5D9] hover:border-[#C05621]"
        >
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-[#EAE5D9] rounded-md p-3">
        <Filter className="w-4 h-4 text-[#8B7355]" />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="audit-action-filter" className="h-9 w-56 bg-white"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All actions</SelectItem>
            {actions.map((a) => <SelectItem key={a} value={a}>{ACTION_META[a]?.label || a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="h-9 w-40 bg-white"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All entities</SelectItem>
            {entityTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant={deletionsOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setDeletionsOnly((v) => !v)}
          data-testid="audit-deletions-only-toggle"
          className={deletionsOnly ? "bg-red-600 hover:bg-red-700 text-white" : "border-red-300 text-red-700 hover:bg-red-50"}
        >
          Deletions only
        </Button>
        <div className="ml-auto text-xs text-[#8B7355]">{visibleItems.length} record{visibleItems.length === 1 ? "" : "s"}</div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
        {visibleItems.length === 0 ? (
          <div className="p-12 text-center text-[#8B7355]"><ScrollText className="w-10 h-10 mx-auto mb-2 opacity-40" />No audit events yet.</div>
        ) : (
          <ul className="divide-y divide-[#EAE5D9]">
            {visibleItems.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, color: "bg-[#F7F2E8] text-[#5C4033]" };
              const open = expanded.has(e.id);
              return (
                <li key={e.id} className="p-4 hover:bg-[#FDFBF7]" data-testid={`audit-row-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggleExpand(e.id)} className="mt-1 shrink-0" data-testid={`audit-expand-${e.id}`}>
                      {open ? <ChevronDown className="w-4 h-4 text-[#8B7355]" /> : <ChevronRight className="w-4 h-4 text-[#8B7355]" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={meta.color + " border-0"}>{meta.label}</Badge>
                        {e.entity_label && <span className="text-sm font-medium">{e.entity_label}</span>}
                        <span className="text-xs text-[#8B7355]">on {e.entity_type}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#8B7355] flex-wrap">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {e.actor_name || "system"} <span className="text-[10px] uppercase tracking-widest">({e.actor_role})</span></span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDateTime(e.at)}</span>
                      </div>
                      {open && e.meta?.changes && <div className="mt-3"><AuditChanges changes={e.meta.changes} /></div>}
                      {open && (e.before || e.after) && (
                        <div className="mt-3 grid md:grid-cols-2 gap-3 text-xs">
                          {e.before && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                              <div className="uc-label !text-red-700 mb-1">Before</div>
                              <pre className="whitespace-pre-wrap break-all font-mono text-red-900">{JSON.stringify(e.before, null, 2)}</pre>
                            </div>
                          )}
                          {e.after && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                              <div className="uc-label !text-emerald-700 mb-1">After</div>
                              <pre className="whitespace-pre-wrap break-all font-mono text-emerald-900">{JSON.stringify(e.after, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
